import { useEffect, useRef } from "react";
import type { LeadFormData } from "../types";

const CAL_NAMESPACE = "free-mockup-strategy";
const CAL_LINK = "mind-media/free-mockup-strategy";
const CAL_ELEMENT_ID = "my-cal-inline-free-mockup-strategy";
const CAL_LOADER_SRC = "https://app.cal.com/embed/embed.js";

interface BookingDetail {
  uid?: string;
  startTime?: string;
  endTime?: string;
  title?: string;
  eventType?: { title?: string };
  booking?: {
    uid?: string;
    startTime?: string;
    endTime?: string;
    title?: string;
  };
  date?: string;
  endDate?: string;
}

interface Props {
  data: LeadFormData;
  onBooked: (booking: NonNullable<LeadFormData["booking"]>) => void;
}

function ensureCalLoader() {
  if (typeof window === "undefined") return;
  (function (C: any, A: string, L: string) {
    const p = function (a: any, ar: any) {
      a.q.push(ar);
    };
    const d = C.document;
    C.Cal =
      C.Cal ||
      function () {
        const cal = C.Cal;
        // eslint-disable-next-line prefer-rest-params
        const ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          d.head.appendChild(d.createElement("script")).src = A;
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api: any = function () {
            // eslint-disable-next-line prefer-rest-params
            p(api, arguments);
          };
          const namespace = ar[1];
          api.q = api.q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
  })(window, CAL_LOADER_SRC, "init");
}

export function StepConfirmationCal({ data, onBooked }: Props) {
  const initializedRef = useRef(false);

  useEffect(() => {
    // Cal.com inline embed auto-scrolls the window when it mounts/resizes.
    // Pin the window to the top on this step so the header/logo stays visible.
    window.scrollTo({ top: 0, behavior: "auto" });
    const timeouts = [100, 400, 900, 1600].map((ms) =>
      window.setTimeout(() => window.scrollTo({ top: 0, behavior: "auto" }), ms),
    );
    return () => timeouts.forEach((t) => window.clearTimeout(t));
  }, []);

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    ensureCalLoader();

    const Cal = window.Cal;
    if (!Cal) return;

    Cal("init", CAL_NAMESPACE, { origin: "https://app.cal.com" });

    Cal.ns[CAL_NAMESPACE]("inline", {
      elementOrSelector: `#${CAL_ELEMENT_ID}`,
      config: {
        layout: "month_view",
        useSlotsViewOnSmallScreen: "true",
        theme: "light",
        name: data.fullName ?? "",
        email: data.email ?? "",
        prefill: {
          notes: data.websiteDescription ?? "",
        },
      },
      calLink: CAL_LINK,
    });

    Cal.ns[CAL_NAMESPACE]("ui", {
      theme: "light",
      hideEventTypeDetails: false,
      layout: "month_view",
    });

    Cal.ns[CAL_NAMESPACE]("on", {
      action: "bookingSuccessful",
      callback: (e: CustomEvent<{ data: BookingDetail }>) => {
        const b = e?.detail?.data ?? {};
        const booking = b.booking ?? {};
        onBooked({
          uid: booking.uid ?? b.uid,
          startTime: booking.startTime ?? b.startTime ?? b.date,
          endTime: booking.endTime ?? b.endTime ?? b.endDate,
          eventTypeTitle: b.eventType?.title ?? booking.title ?? b.title,
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="w-full min-w-0">
      <div
        id={CAL_ELEMENT_ID}
        className="w-full bg-transparent lg:-mt-[64px]"
        style={{ minHeight: 680 }}
      />
    </div>
  );
}
